/**
 * @file Scene lighting rig.
 *
 * A fixed three-light setup (ambient fill + key with shadows + coloured rim).
 * The key light is directional and its shadow camera is sized from the arena,
 * so it stays a single shadow pass regardless of entity count.
 */

import * as THREE from 'three';

export class Lighting {
  /**
   * @param {THREE.Scene} scene Scene to populate.
   * @param {import('../config/GameConfig.js').GameConfig} config Game config.
   */
  constructor(scene, config) {
    const settings = config.lighting;

    /** @type {THREE.Group} */
    this.group = new THREE.Group();
    this.group.name = 'Lighting';

    /** @type {THREE.AmbientLight} */
    this.ambient = new THREE.AmbientLight(settings.ambient.color, settings.ambient.intensity);

    /** @type {THREE.HemisphereLight} */
    this.hemisphere = new THREE.HemisphereLight(
      settings.hemi.sky,
      settings.hemi.ground,
      settings.hemi.intensity
    );

    /** @type {THREE.DirectionalLight} */
    this.key = new THREE.DirectionalLight(settings.key.color, settings.key.intensity);
    this.key.position.set(...settings.key.position);
    this.key.castShadow = config.renderer.shadows;

    const extent = settings.key.shadowExtent;
    const shadowCamera = /** @type {THREE.OrthographicCamera} */ (this.key.shadow.camera);
    shadowCamera.left = -extent;
    shadowCamera.right = extent;
    shadowCamera.top = extent;
    shadowCamera.bottom = -extent;
    shadowCamera.near = 1;
    shadowCamera.far = 120;
    shadowCamera.updateProjectionMatrix();

    this.key.shadow.mapSize.set(settings.key.shadowMapSize, settings.key.shadowMapSize);
    this.key.shadow.bias = -0.0006;
    this.key.shadow.normalBias = 0.03;

    /** @type {THREE.DirectionalLight} */
    this.rim = new THREE.DirectionalLight(settings.rim.color, settings.rim.intensity);
    this.rim.position.set(...settings.rim.position);

    this.group.add(this.ambient, this.hemisphere, this.key, this.key.target, this.rim);
    scene.add(this.group);
  }

  /**
   * Keeps the shadow frustum centred on a point so shadow resolution follows
   * the action instead of being spread across the whole arena.
   * @param {THREE.Vector3} target Point to centre on, usually the player.
   * @returns {void}
   */
  focusOn(target) {
    this.key.target.position.copy(target);
    this.key.target.updateMatrixWorld();
  }

  /**
   * @returns {void}
   */
  dispose() {
    this.group.removeFromParent();
    this.ambient.dispose();
    this.hemisphere.dispose();
    this.key.dispose();
    this.rim.dispose();
  }
}
